import React, { useRef, useState, useEffect } from 'react'
import classnames from 'classnames'

import { dataTestId } from '@hip/test-attributes'
import { Button } from '@hip/components/src/components/button/button'
import { Text, TextEl } from '@hip/components/src/components/text/text'
import { Icon } from '@hip/components/src/components/icon/icon'
import { usePrevious } from '@hip/hooks'
import { IconSize } from '@hip/components/src/components/icon'

import styles from './modal.module.css'

interface ModalProps {
  show: boolean
  onClose?: () => void
  title?: string
  rootClasses?: string
  headerClasses?: string
  contentClasses?: string
  autoFocusCloseButton?: boolean
  verticalAlignCenter?: boolean
  customizations?: {
    iconSize?: IconSize
    titleEl?: TextEl
  }
}

const KEY_ESC = 'Escape'

export const Modal: React.FC<ModalProps> = ({
  show,
  onClose,
  title = '',
  children,
  rootClasses,
  headerClasses,
  contentClasses,
  autoFocusCloseButton = true,
  verticalAlignCenter = false,
  customizations,
}) => {
  // Close on press ESC
  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === KEY_ESC) {
        // Note: in CDT all modals are in DOM, so that we need check is current
        // modal is showed to prevent conflicts with other modals handlers
        if (show && onClose) onClose()
      }
    }
    document.addEventListener('keydown', handleKey, false)
    return () => document.removeEventListener('keydown', handleKey, false)
  }, [onClose, show])

  // Auto focus on close button
  const prevShow = usePrevious(show)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [needFocus, setNeedFocus] = useState(false)

  useEffect(() => {
    if (autoFocusCloseButton && !prevShow && show) {
      setNeedFocus(true)
    }
  }, [prevShow, show, autoFocusCloseButton])

  useEffect(() => {
    if (needFocus) {
      const timer = setInterval(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus()
          setNeedFocus(false)
          clearInterval(timer)
        }
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [needFocus])

  const modalHeaderId = title
    ? `${title.split(' ').join('-')}-modal-header`
    : 'modal-header'

  return show ? (
    <div
      className={classnames(
        styles.ModalOverlay,
        verticalAlignCenter && styles.OverlayVerticalAlign
      )}
      onClick={onClose}
      data-test-id={dataTestId.component.modal.overlay}
    >
      <div
        className={classnames(styles.Modal, rootClasses)}
        onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        data-test-id={dataTestId.component.modal.container}
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalHeaderId}
      >
        <div
          className={classnames(styles.ModalHeader, headerClasses)}
          data-test-id={dataTestId.component.modal.header}
        >
          <Text
            vMargin="none"
            el={customizations?.titleEl ?? 'h2'}
            id={modalHeaderId}
          >
            {title}
          </Text>
          {onClose && (
            <Button
              type="naked"
              classes={styles.CloseButton}
              onClick={onClose}
              dataTestId={dataTestId.component.modal.btnClose}
              ref={closeButtonRef}
              ariaLabel="Close"
            >
              <Icon icon="CLOSE" size={customizations?.iconSize} />
            </Button>
          )}
        </div>
        <div className={classnames(styles.ModalContent, contentClasses)}>
          {children}
        </div>
      </div>
    </div>
  ) : null
}
